import { apiService } from './apiService';
import { BOT_REMOVAL_OUTCOME, BotRemovalOutcome } from '../constants/botApi';
import { BotRemovalResponse, isBotGoneError, removalOutcomeOf } from '../utils/botApiErrors';

/**
 * The web's calls to the backend's /api/bots routes. All go through apiService,
 * which sends a fresh Firebase ID token as `Authorization: Bearer` (the backend
 * requires it; terminate also requires the bot's owner). A non-2xx response
 * rejects with apiService's ApiError, which carries the HTTP status
 * (utils/botApiErrors.ts turns 401/403 into user-facing copy).
 *
 * Bot creation keeps its own call in MeetBotModal (it already sends the token).
 */

interface ActiveSessionsPayload<T> {
  sessions?: T[];
}

/** One live bot call in GET /api/bots/active (backend `routes/bots.js`). */
export interface ActiveBotSession {
  botId?: string;
  sessionName?: string;
  ownerName?: string | null;
  ownerEmail?: string | null;
  startedAt?: string | null;
  websocketUrl?: string | null;
  isOwn?: boolean;
  abandoned?: boolean;
}

const botPath = (botId: string, action: string): string => `/bots/${encodeURIComponent(botId)}/${action}`;

/**
 * Remove Bot for a manually added bot (owner only), by its Recall bot id: the
 * backend resolves it on any Lambda container, where the create response's
 * in-memory session id lives only in the one that created it. The backend
 * decides whether the bot was still in the call: `alreadyEnded` when it says
 * so, or when it no longer knows the bot (404). Rejects (ApiError) for
 * anything else, e.g. 401 / 403 / 500.
 */
export const terminateManualBot = async (botId: string): Promise<BotRemovalOutcome> => {
  try {
    const response = (await apiService.post(botPath(botId, 'terminate'))) as BotRemovalResponse | null;
    return removalOutcomeOf(response);
  } catch (error) {
    if (isBotGoneError(error)) return BOT_REMOVAL_OUTCOME.alreadyEnded;
    throw error;
  }
};

/** Point a bot's session at this browser's client id. */
export const updateBotClient = (botId: string, clientId: string) =>
  apiService.patch(botPath(botId, 'client'), { clientId });

/** Every live bot call in the tenant, the user's own flagged `isOwn` (the Watch a Session list). */
export const fetchActiveBotSessions = async <T>(): Promise<T[]> => {
  const payload = (await apiService.get('/bots/active')) as unknown as ActiveSessionsPayload<T> | null;
  return payload?.sessions ?? [];
};
